import { AutogenLayoutStatusProfile } from "./autogen/AutogenLayoutStatusProfile";
import { EDITemplateUpdateProps } from "./EDITemplateSlideout";
import { BlurEvent, Button, ChangeEvent, Checkbox, ClickEvent, CrudDecorator, DataHeaderProps, DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, Layout, LookupModelSearchEvent, SlideoutDecorator, TableRow, TableRowCreationEvent, TableRowDisplayEvent, Textbox, TableRowMoveEvent, ValidationResult, LookupModelSelectionEvent, DataSourceValidationEvent, DataSourceAction, Snackbar } from "@mcleod/components";
import { BaseProfile } from "./BaseProfile";
import { Api, McLeodClassicIntegration, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { createCustomizationSlideout, handleButtonMasterBatchClick, handleMasterBatchCheck, setFuncAckTemplateValues, setTemplateValues } from "./EdiUtility";
import { TransmissionSchedule } from "./TransmissionSchedule";
import { EdiShipMatchCrit } from "./EdiShipMatchCrit";
import { CrudDecoratorProps } from "@mcleod/components/src/page/decorators/CrudDecoratorProps";
import { CommonDialogs } from "@mcleod/common";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { ModelEdistatusProfile } from "./models/ModelEdistatusProfile";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { handleCommIdSelection } from "./DataFusionUtil";

const layoutURL = "lme/datafusion/StatusProfile";

export class StatusProfile extends AutogenLayoutStatusProfile {
    private STATUS = "S";
    private baseProfile: BaseProfile;
    private static readonly MINUTES_PER_HOUR = 60;
    private static readonly INTERVAL_HOURS_FIELD = "interval_hours";
    private static readonly INTERVAL_MINUTES_FIELD = "interval_minutes";
    private OUTBOUND = "O";
    private INBOUND = "I";
    private FUNCTIONAL_ACK = "F";

    override async onLoad() {
        // Initialize BaseProfile with required context
        this.baseProfile = new BaseProfile({
            mainDataSource: this.mainDataSource,
            textboxSendToCompany: this.textboxSendToCompany,
            textboxCommId: this.textboxCommId,
            tableRequiredField: this.tableRequiredField,
            INBOUND: this.INBOUND,
            OUTBOUND: this.OUTBOUND
        });

        await this.baseProfile.initializeIntercompanySupport();
        this.checkboxIntercompany.visible = this.baseProfile.isIcEnabled;
        this.baseProfile.setupTableRequiredFieldHandlers("214", false);

        this.tableShipMatchCrit.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            const layoutName = "lme/datafusion/EdiShipMatchCrit";
            if (layoutName == null) {
                return;
            }
            const layout = Layout.getLayout(layoutName) as EdiShipMatchCrit;
            layout.profileId = this.mainDataSource?.activeRow?.get("id");
            layout.version = this.mainDataSource?.activeRow?.get("version");
            layout.partnerId = this.mainDataSource?.activeRow?.get("partner_id");

            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    title: "Add Shipment Matching Criteria",
                    doAfterClose: () => { },
                    ...dataHeaderProps,
                },
                doAfterSlideOut: (decorator) => {
                    // Resequence all rows after any CRUD operation
                    this.tableShipMatchCrit.allRows.forEach((row, index) => {
                        const textboxSequence = row.findComponentById("textboxShipMatchSequence") as Textbox;
                        textboxSequence.text = (index + 1).toString();
                        row.data.set("sequence", index + 1);
                    });
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        };

        this.tableShipMatchCrit.addButtonCaption = "Add";

        // Setup the proper validation callback for dialog prompts
        this.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
            // Check if we're in ADD mode and ask if user wants to add default events
            if (this.mainDataSource?.mode === DataSourceMode.ADD &&
                this.mainDataSource?.activeRow?.get("partner_id") &&
                this.mainDataSource?.activeRow?.get("alt_partner_id") &&
                this.mainDataSource?.activeRow?.get("version") &&
                this.mainDataSource?.activeRow?.get("company_id") &&
                this.mainDataSource?.activeRow?.get("direction")) {

                const validationResults: ValidationResult[] = this.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                    return false;
                }

                const partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                const altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
                const version = this.mainDataSource?.activeRow?.get("version");
                const companyId = this.mainDataSource?.activeRow?.get("company_id");
                const direction = this.mainDataSource?.activeRow?.get("direction");
                const transactionSet = this.mainDataSource?.activeRow?.get("transaction_set", "214");
                if (await this.profileExists(partnerId, altPartnerId, version, companyId, direction, transactionSet)) {
                    CommonDialogs.showError("Partner profile already exists");
                    return false;
                }
                if (partnerId && altPartnerId && version && companyId && direction && transactionSet) {
                    try {
                        const result = await CommonDialogs.showYesNo(
                            "Would you like to add the default set of events to this profile?",
                            "Add Default Events"
                        );

                        if (result) {
                            await this.generateDefaultEvents(partnerId, altPartnerId, version, companyId, direction, transactionSet);
                        }
                    } catch (error) {
                        console.error("Error in dialog prompt:", error);
                        // Return false to prevent the save if there was an error
                        return false;
                    }
                }
            }

            // Return true to allow the save to proceed
            return true;
        };
        this.textboxTransactionSet.items = null;
    }

    public static navigateTo(recordId: string) {
        const url = layoutURL + `?mode=update&key=${recordId}`;
        Navigation.navigateTo(url, { newTab: true })
    }

    onChangeIntercompany(event: ChangeEvent) {
        this.baseProfile.handleIntercompanyChange(event, (enabled) => {
            this.baseProfile.setCompanyField(enabled);
            const isRequired = !enabled && this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND;
            this.textboxCommId.required = isRequired;
        });
    }


    setupButtonOnClick() {
        this.baseProfile.setupTemplateSlideout("template_id", (returnedValues) => {
            this.updateFromTemplate(returnedValues);
        });
    }


    updateFromTemplate(updatedValues: EDITemplateUpdateProps) {
        this.baseProfile.updateFromTemplate(updatedValues);
        // Update display after template change
        this.textboxTemplateId.displayData(this.mainDataSource?.activeRow, null, 0);
    }

    doBeforeModeChange() {
        this.setTitle();
    }

    onValidate(event: DataSourceValidationEvent) {
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND && this.checkboxIntercompany.checked) {
            this.mainDataSource?.activeRow?.set("transmit_method", "X");
        }
    }

    async afterExecution(event: DataSourceExecutionEvent) {
        this.tabsetStatusProfile.visible = this.mainDataSource?.mode !== DataSourceMode.ADD;
        this.baseProfile.setCompanyField(this.mainDataSource?.activeRow?.getBoolean("intercompany") && this.baseProfile.isIcEnabled);
        const direction = this.mainDataSource?.activeRow?.get("direction");
        this.setNextBatchFields();
        this.updateUIVisibility(direction);
        if (event && event.getAction() === DataSourceAction.UPDATE && this.mainDataSource?.activeRow?.get("template_id")) {
            this.buttonSetup.enabled = true;
        }
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
        if (this.OUTBOUND === direction) {
            this.layoutInboundFuncAck.mainDataSource.search({
                alt_partner_id: this.mainDataSource?.activeRow?.get("alt_partner_id"),
                partner_id: this.mainDataSource?.activeRow?.get("partner_id"),
                version: this.mainDataSource?.activeRow?.get("version")
            });
        }
        this.layoutEdiPartnerQualifiers.setPartnerValues(this.mainDataSource?.activeRow?.get("partner_id"), this.mainDataSource?.activeRow?.get("version"));
        this.layoutEdiPartnerQualifiers.setColumnsForTender();
        this.layoutEdiDelayCodePartners.setPartnerValues(this.mainDataSource?.activeRow?.get("partner_id"), this.mainDataSource?.activeRow?.get("version"));
        this.layoutEdiDelayCodePartners.setColumnsForStatus();
        this.buttonSchedule.enabled = this.mainDataSource?.activeRow?.get("batch_method") === "S" || this.mainDataSource?.activeRow?.get("batch_method") === "E";
        this.layoutStatusEvent.profileId = this.mainDataSource?.activeRow?.get("id");
        this.layoutStatusEvent.version = this.mainDataSource?.activeRow?.get("version");
        this.layoutStatusEvent.partnerId = this.mainDataSource?.activeRow?.get("partner_id");
        this.layoutStatusEvent.direction = this.mainDataSource?.activeRow?.get("direction");
        this.layoutStatusEvent.altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
        this.textboxCommId.required = this.OUTBOUND === direction && !this.checkboxIntercompany.checked;
    }

    setCompanySettings() {
        this.baseProfile.setCompanySettings();
        const isLtl = this.baseProfile.isAssetLtl || this.baseProfile.isBrokerageLtlCompany;
        this.layoutHandlingRequirementXref.visible = isLtl;
        this.layoutPackagingTypeCode.visible = isLtl;
    }

    tableEdiCustomerOnRowCreate(event: TableRowCreationEvent) {
        const newRow = event.getTableRow().data;
        // Reset original data FIRST, before setting values, to prevent marking datasource as changed
        newRow.resetOriginalData();

        this.baseProfile.handleEdiCustomerRowCreate(event, "S");

        // Reset again after setting to clear the dirty flag
        newRow.resetOriginalData();
        // Force the datasource to recalculate its hasChanged state
        this.sourceEdiCommodity?.notifyHasChangedComponents(false);
    }

    tableRequiredFieldOnRowCreate(event: TableRowCreationEvent) {
        this.baseProfile.handleRequiredFieldRowCreate(event, "214", "O");
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen(
            "com.tms.client.loadmaster.edi.EntryEdiStatusProfile",
            `<ext> search id ='${this.mainDataSource?.activeRow?.get("id")}'`
        );
    }

    intervalHoursOnBlur(event: BlurEvent) {
        this.intervalValueOnBlur(event, true);
    }

    internalMinutesOnBlur(event: BlurEvent) {
        this.intervalValueOnBlur(event, false);
    }


    updateAuditInterval(hoursValueInput: number, minutesValueInput: number) {
        const totalMinutes = hoursValueInput * StatusProfile.MINUTES_PER_HOUR + minutesValueInput;
        this.mainDataSource?.activeRow?.set("audit_interval", totalMinutes);
        let intervalHours = hoursValueInput;
        let intervalMinutes = minutesValueInput;
        if (minutesValueInput >= StatusProfile.MINUTES_PER_HOUR) {
            intervalHours += Math.floor(minutesValueInput / StatusProfile.MINUTES_PER_HOUR);
            intervalMinutes = minutesValueInput % StatusProfile.MINUTES_PER_HOUR;
        }
        this.mainDataSource?.activeRow?.set("interval_hours", intervalHours);
        this.mainDataSource?.activeRow?.set("interval_minutes", intervalMinutes);
    }

    buttonCustomFilenameOnClick() {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Status Filename",
            this.mainDataSource,
            "filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    buttonCustomizeTransControNoiOnClick() {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Status Transmission Control Number",
            this.mainDataSource,
            "st02_format",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    textboxBatchMethodOnSelection(event: DropdownSelectionEvent) {
        const textbox = event.target as Textbox;
        if (event.userInitiatedChange) {
            this.setBatchMethod();
        }
        this.buttonSchedule.enabled = textbox.selectedItem.value === "S" || textbox.selectedItem.value === "E";
    }

    templateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.STATUS, this.mainDataSource?.activeRow?.get("direction"));
    }

    communicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.STATUS);
    }
    async templateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setTemplateValues(template, this.mainDataSource);
            }
        }
    }

    /** This is an event handler for the onChange event of checkboxIndividualBatch. */
    checkboxIndividualBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && this.checkboxIndividualBatch.checked && this.checkboxUseMBatch.checked) {
            this.checkboxIndividualBatch.checked = false;
            Snackbar.showWarningSnackbar("Individual Batching and Use Master Batch cannot both be selected.");
        }
    }

    /** This is an event handler for the onChange event of checkboxMasterBatch. */
    async checkboxMasterBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && this.checkboxUseMBatch.checked && this.checkboxIndividualBatch.checked) {
            this.checkboxUseMBatch.checked = false;
            Snackbar.showWarningSnackbar("Individual Batching and Use Master Batch cannot both be selected.");
            return;
        }
        await this.masterBatchOnChange(event);
    }

    /** This is an event handler for the onClick event of buttonMasterBatch. */
    async buttonMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxUseMBatch.checked = true;
        });
    }

    private templateBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string, direction: string) {
        event.filter.direction = direction;
        event.filter.transaction_type = type;
    }

    private commBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string) {
        event.filter.direction_input = this.mainDataSource?.activeRow?.get("direction");
        event.filter.type = type;
    }

    private setBatchMethod() {
        const batchMethod = this.mainDataSource?.activeRow?.get("batch_method");
        this.sourceEdistatuseventTable.data.forEach((row) => {
            row.set("batch_method_read_only", batchMethod);
        });
    }


    private intervalValueOnBlur(event: BlurEvent, isHours: boolean) {
        if (event.changedWhileFocused) {
            const textbox = event.target as Textbox;
            if (StringUtil.isEmptyString(textbox.text)) {
                if (isHours) {
                    this.mainDataSource?.activeRow?.set(StatusProfile.INTERVAL_HOURS_FIELD, 0);
                } else {
                    this.mainDataSource?.activeRow?.set(StatusProfile.INTERVAL_MINUTES_FIELD, 0);
                }
            }
            this.updateAuditInterval(
                this.mainDataSource?.activeRow?.getNumber(StatusProfile.INTERVAL_HOURS_FIELD) ?? 0,
                this.mainDataSource?.activeRow?.getNumber(StatusProfile.INTERVAL_MINUTES_FIELD) ?? 0
            );
        }
    }


    private updateUIVisibility(direction: string) {
        const isInbound = direction === this.INBOUND;

        this.layoutEdiNotify.tableEdiNotifyInbound.visible = isInbound;
        this.layoutEdiNotify.tableEdiNotifyOutbound.visible = !isInbound;
        this.panelInboundFuncAck.visible = !isInbound;
        this.panelGeneralOutbound.visible = !isInbound;
        this.tabCustomers.visible = !isInbound;
        this.tabPartnerQual.visible = !isInbound;
        this.tabDataValidation.visible = !isInbound;
        this.panelOutboundFuncAck.visible = isInbound;
        this.panelGeneralInbound.visible = isInbound;
        this.tabCarrier.visible = isInbound;
        this.tabShipmentMatching.visible = isInbound;
        this.buttonSchedule.visible = !isInbound;
        this.setCompanySettings();

        this.setCrossReferenceVisibility(isInbound);

    }
    private setCrossReferenceVisibility(isInbound: boolean) {
        // Set cross-reference layout visibility based on direction
        if (isInbound) {
            // For inbound direction, only show specific layouts
            this.layoutLocationCustomerXref.visible = true;
            this.layoutCommodityXref.visible = true;

            // Only show handling requirements and packaging type if PBLTL enabled
            const isLtl = this.baseProfile.isAssetLtl || this.baseProfile.isBrokerageLtlCompany;
            this.layoutHandlingRequirementXref.visible = isLtl;
            this.layoutPackagingTypeCode.visible = isLtl;

            // Hide other cross-reference layouts for inbound
            this.layoutTrailerType.visible = false;
        } else {
            // For outbound direction, use existing company settings logic
            this.layoutLocationCustomerXref.visible = true;
            this.layoutCommodityXref.visible = true;
            this.layoutTrailerType.visible = true;
        }
    }

    /** This is an event handler for the onClick event of buttonSchedule. */
    buttonScheduleOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/datafusion/TransmissionSchedule") as TransmissionSchedule;
        const saveButton = new Button({
            caption: "Save & Close",
            rowBreak: false,
            color: "primary.reverse",
            backgroundColor: "primary",
            onClick: async (event: ClickEvent) => {
                saveButton.busy = true;
                layout.sourceEdischedule.post().then(() => {
                    saveButton.busy = false;
                    layout.slideOut();
                });

            }
        });
        const sod = new SlideoutDecorator({
            layout: layout,
            title: "Shipment Status Transmission Schedule",
            width: '75%',
            addlComponents: [saveButton],
            fillVerticalSpace: true,
            doAfterSlideIn: async () => {
                layout.parentRowID = this.mainDataSource.activeRow.get("id");
                layout.altPartnerID = this.mainDataSource.activeRow.get("alt_partner_id");
                layout.version = this.mainDataSource.activeRow.get("version");
                layout.partnerId = this.mainDataSource.activeRow.get("partner_id");
                layout.transactionType = "S";
                layout.transactionSet = "214";
                layout.parentRowType = "P";
                layout.mainDataSource.search({ parent_row_id: this.mainDataSource.activeRow.get("id") });

            }

        });
    }

    /** This is an event handler for the onClick event of buttonFuncAckMasterBatch. */
    async buttonFuncAckMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxFuncUseMBatch.checked = true;
        });
    }

    async funcAckTemplateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setFuncAckTemplateValues(template, this.mainDataSource);
            }
        }
    }

    funcAckTemplateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK, this.OUTBOUND);
    }

    setupFuncAckButtonOnClick() {
        this.baseProfile.setupTemplateSlideout("func_ack_tid", (returnedValues) => {
            this.updateFuncAckFromTemplate(returnedValues);
        });
    }
    updateFuncAckFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxFuncAckTid.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("func_ack_tid"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("func_ack_tid", lookupModelRow);
        this.textboxFuncAckTid.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("func_version", updatedValues.version);
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND) {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.receiverId);
        }
    }
    funcAckCommunicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK);
    }
    /** This is an event handler for the onClick event of buttonFilenameCustomize. */
    buttonFilenameCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Functional Acknowledgement Filename",
            this.mainDataSource,
            "func_ack_filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }
    /** This is an event handler for the onClick event of buttonTransactionCustomize. */
    buttonTransactionCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Functional Acknowledgement Transmission Control Number",
            this.mainDataSource,
            "func_ack_st02_frmt",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }
    /** This is an event handler for the onRowCreate event of tableEdiCarrier. */
    tableEdiCarrierOnRowCreate(event: TableRowCreationEvent) {
        const newRow = event.getTableRow().data;
        // Reset original data FIRST, before setting values, to prevent marking datasource as changed
        newRow.resetOriginalData();
        this.baseProfile.handleEdiCustomerRowCreate(event, "S");
        // Reset again after setting to clear the dirty flag
        newRow.resetOriginalData();
        // Force the datasource to recalculate its hasChanged state
        this.sourceEdiCommodity?.notifyHasChangedComponents(false);
    }
    /** This is an event handler for the beforeLookupModelSearch event of textboxEDiCarrier. */
    textboxEDiCarrierBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const carrierTextBox = tableRow?.findComponentById("textboxEDiCarrier") as Textbox;
        event.filter.status = "A";
        if (carrierTextBox) {
            event.filter.lm_search = carrierTextBox.text;
        }
    }

    /** This is an event handler for the onRowDisplay event of tableShipMatchCrit. */
    tableShipMatchCritOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.getTableRow() as TableRow;
        this.updateShipMatchCritRowSequence(row);
    }

    /**
     * Updates the sequence for a single shipment match criteria row based on its position
     * @param row The table row to update
     */
    private updateShipMatchCritRowSequence(row: TableRow) {
        // Set the sequence based on the row's position in the table (1-based)
        const allRows = this.tableShipMatchCrit.allRows;
        const rowIndex = allRows.indexOf(row);
        if (rowIndex >= 0) {
            const sequenceValue = rowIndex + 1;
            row.data.set("sequence", sequenceValue);

            // Update any sequence textbox in the row if it exists
            const sequenceTextbox = row.findComponentById("textboxSequence") as Textbox;
            if (sequenceTextbox) {
                sequenceTextbox.text = sequenceValue.toString();
            }
        }
    }

    /** This is an event handler for the onRowCreate event of tableShipMatchCrit. */
    tableShipMatchCritOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow() as TableRow;

        if (tableRow.table.editLayout) {
            // Override the edit layout loading process
            const originalEditLayoutLoaded = tableRow.table.onEditLayoutLoaded;
            tableRow.table.onEditLayoutLoaded = (decorator, row) => {
                const criteriaLayout = decorator.layout as EdiShipMatchCrit;
                criteriaLayout.version = this.mainDataSource?.activeRow?.get("version");
                criteriaLayout.profileId = this.mainDataSource?.activeRow?.get("id");
                criteriaLayout.partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                criteriaLayout.sourceEdiShipmatchCrit.activeRow?.set("version", this.mainDataSource?.activeRow?.get("version"));
                const layout = decorator.layout;
                if (criteriaLayout.sourceEdiShipmatchCrit.activeRow._appending) {
                    criteriaLayout.sourceEdiShipmatchCrit.activeRow.set("profile_id", this.mainDataSource?.activeRow?.get("id"));
                    // Don't set sequence here - let it be handled by doAfterSlideOut and onRowDisplay
                }
                // Hide Save & Add Another option in the multibutton for edit layout
                if (decorator.multiButton && decorator.multiButton.components && decorator.multiButton.components.length > 1) {
                    decorator.multiButton.components[1].visible = false;
                }

                // Call the original handler if it exists
                if (originalEditLayoutLoaded) {
                    originalEditLayoutLoaded(decorator, row);
                }
                const superOnSave = decorator.onSave;
                decorator.onSave = (updatedRow: any) => {
                    if (decorator.layout.mainDataSource.validate()) {
                        const rules = criteriaLayout.tableShipMatchRule?.data;
                        updatedRow.set('shipment_match_rules', rules);

                        // Update sequence for the newly created row after save
                        setTimeout(() => {
                            const newRow = this.tableShipMatchCrit.allRows.find(r => r.data === updatedRow);
                            if (newRow) {
                                this.updateShipMatchCritRowSequence(newRow);
                            }
                        }, 0);

                        superOnSave(updatedRow);
                    }
                };
            };
        }
    }

    /** This is an event handler for the afterTableRowMove event of tableShipMatchCrit. */
    tableShipMatchCritAfterTableRowMove(event: TableRowMoveEvent) {
        // Only update sequences when rows are actually moved, not added
        this.tableShipMatchCrit.allRows.forEach((row, index) => {
            row.data.set("sequence", index + 1);
        });
        this.mainDataSource.mode = DataSourceMode.UPDATE;
    }

    async doAfterModeChange() {
        this.activeRow?.addAfterFieldUpdateListener((event) => {
            this.layoutEdiDelayCodePartners.setEnabledButtonEnabledProperties(!this.activeRow.hasChanged());
        });
        this.setTitle();
    }

    /** This is an event handler for the onChange event of textboxFuncAckTid. */
    textboxFuncAckTidOnChange(event: ChangeEvent) {
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
    }

    private async generateDefaultEvents(partnerId: string, altPartnerId: string, version: string, companyId: string, direction: string, transactionSet: string): Promise<void> {
        // Call the generate status event defaults endpoint
        try {
            return await Api.post("lme/datafusion/generate-status-event-defaults",
                {
                    partner_id: partnerId,
                    alt_partner_id: altPartnerId,
                    version: version,
                    company_id: companyId,
                    direction: direction,
                    transaction_set: transactionSet
                });
        } catch (error) {
            console.error("Error generating default status events:", error);
        }
    }

    async checkboxMasterFuncBatchOnChange(event: ChangeEvent) {
        await this.masterBatchOnChange(event)
    }

    private setNextBatchFields() {
        if (!this.checkboxFuncUseMBatch.checked) {
            this.textboxFuncNextBatch.visible = true;
            this.labelNextBatchNumber.visible = false;
            this.labelFuncMasterBatch.visible = false;
        } else {
            this.textboxFuncNextBatch.visible = false;
            this.labelNextBatchNumber.visible = true;
            this.labelFuncMasterBatch.visible = true;
        }
    }

    setTitle() {
        this.dataHeader.title = (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND ? "Inbound " : "Outbound ") + "Shipment Status Profile";
    }

    async profileExists(profileId: string, altPartnerId: string, version: string, companyId: string, direction: string, transactionSet: string): Promise<boolean> {
        const profile = await new ModelEdistatusProfile().searchSingle({
            partner_id: profileId,
            alt_partner_id: altPartnerId,
            version: version,
            company_id: companyId,
            direction: direction,
            transaction_set: transactionSet
        });
        return profile != null;
    }

    private async masterBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const check = event.target as Checkbox;
            const partnerName = this.mainDataSource.activeRow.get("partner_name");
            const partnerID = this.mainDataSource.activeRow.get("partner_id");
            const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
            const version = this.mainDataSource.activeRow.get("version");

            await handleMasterBatchCheck(check, partnerName, partnerID, altPartnerId, version);
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCommId. */
    textboxCommIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        if (this.mainDataSource?.activeRow.get("direction") === "O") {
            handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "214");
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxFuncCommId. */
    textboxFuncCommIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "997");
    }

    /** This is an event handler for the onChange event of textboxTemplateId. */
    textboxTemplateIdOnChange(event: ChangeEvent) {
        this.buttonSetup.enabled = !StringUtil.isEmptyString(this.textboxTemplateId.text);
    }

    setAuditRequiredFields(direction: string) {
        this.textboxIntervalHours.required = direction === this.OUTBOUND;
        this.textboxIntervalMinutes.required = direction === this.OUTBOUND;
        this.textboxAuditCutoffDays.required = direction === this.OUTBOUND;
    }

/** This is an event handler for the beforeExecution event of sourceEdiRequiredField. */
  sourceEdiRequiredFieldBeforeExecution(event: DataSourceExecutionEvent) {
    event.filter.event_level = false;
  }
}
